import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DateInput from './DateInput';
import FormLabel from './CustomFormLabel';
import { PRECISION, localDateFromParts } from 'src/util/partialDate';
import { formatAgeDisplay } from 'src/util/util';

const MIN_YEAR = 1850;
const MAX_YEAR = new Date().getFullYear() + 1;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const isValidYear = (year) =>
  Number.isInteger(year) && year >= MIN_YEAR && year <= MAX_YEAR;

/**
 * Date input that captures user-knowable precision.
 *
 * Default mode renders the standard `DateInput` for full DD/MM/YYYY entry.
 * Checking "Exact Date Unknown" reveals a precision selector with three
 * tabs — Year only / Month + year / Full date — with the user's previously
 * entered values preserved across toggles.
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {import('src/util/partialDate').PartialDate|null} props.value
 * @param {(value: import('src/util/partialDate').PartialDate|null) => void} props.onChange
 * @param {string} [props.ageLabel]    e.g. "Current age", "Age at death"
 * @param {import('src/util/util').AgeResult|null} [props.ageResult]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.required]
 * @param {boolean} [props.forceYearOnly]
 *   Hard-lock to year precision (drives `saveDateAsYears` flow). The checkbox
 *   is always checked + disabled and only the year input renders.
 * @param {boolean} [props.disableFuture]
 * @param {string|Date} [props.minDate]
 */
export default function PartialDateInput({
  label,
  value,
  onChange,
  ageLabel,
  ageResult,
  disabled,
  required,
  forceYearOnly,
  disableFuture,
  minDate
}) {
  const externalUnknown = value
    ? value.precision !== PRECISION.FULL
    : !!forceYearOnly;
  const [unknownChecked, setUnknownChecked] = React.useState(externalUnknown);

  // Sync local checkbox state with external value changes (e.g. parent
  // reload, node switch). This is authoritative — when the parent passes a
  // new `value`, the component's UI state realigns with it. Without this,
  // partial-precision state from a previously-edited node could leak into
  // a freshly-selected node and silently corrupt the next save.
  React.useEffect(() => {
    if (forceYearOnly) {
      setUnknownChecked(true);
      return;
    }
    if (value && value.precision !== PRECISION.FULL) {
      setUnknownChecked(true);
    } else {
      setUnknownChecked(false);
    }
  }, [value, forceYearOnly]);

  // Snapshot of the most recent partial value so toggling the checkbox
  // preserves entry. Reset when value clears so a stale snapshot from a
  // previous node doesn't pre-fill a new node's input.
  const lastSnapshotRef = React.useRef(value);
  React.useEffect(() => {
    lastSnapshotRef.current = value || null;
  }, [value]);

  // Active tab when in partial-precision mode.
  const tabPrecision =
    value && value.precision !== PRECISION.FULL
      ? value.precision
      : PRECISION.YEAR;
  const [activeTab, setActiveTab] = React.useState(tabPrecision);

  React.useEffect(() => {
    if (forceYearOnly) {
      setActiveTab(PRECISION.YEAR);
    } else if (value && value.precision !== PRECISION.FULL) {
      setActiveTab(value.precision);
    } else {
      // Empty value or full precision: reset to YEAR so a fresh node doesn't
      // inherit a previous node's tab selection.
      setActiveTab(PRECISION.YEAR);
    }
  }, [value, forceYearOnly]);

  const emit = (next) => {
    if (next) lastSnapshotRef.current = next;
    onChange(next);
  };

  const handleToggleUnknown = (e) => {
    if (forceYearOnly) return;
    const checked = e.target.checked;
    setUnknownChecked(checked);
    if (checked) {
      const snap = lastSnapshotRef.current;
      const year = snap?.year ?? null;
      if (year && isValidYear(year)) {
        emit({ year, month: null, day: null, precision: PRECISION.YEAR });
      } else {
        emit(null);
      }
      setActiveTab(PRECISION.YEAR);
    } else {
      const snap = lastSnapshotRef.current;
      // Keep year (and month if previously known) but clear precision so the
      // full date picker can re-collect day. If we have a complete snapshot
      // already, restore it directly.
      if (snap?.precision === PRECISION.FULL) {
        emit(snap);
      } else if (snap?.year) {
        emit({
          year: snap.year,
          month: snap.month ?? null,
          day: null,
          precision: PRECISION.FULL
        });
      } else {
        emit(null);
      }
    }
  };

  const handleTabChange = (_, next) => {
    setActiveTab(next);
    const snap = lastSnapshotRef.current;
    const year = snap?.year ?? null;
    if (next === PRECISION.YEAR) {
      if (year && isValidYear(year)) {
        emit({ year, month: null, day: null, precision: PRECISION.YEAR });
      } else {
        emit(null);
      }
    } else if (next === PRECISION.MONTH) {
      if (year && isValidYear(year)) {
        emit({
          year,
          month: snap?.month ?? null,
          day: null,
          precision: PRECISION.MONTH
        });
      } else {
        emit(null);
      }
    } else if (next === PRECISION.FULL) {
      // Switching to full date inside the partial flow auto-unchecks.
      setUnknownChecked(false);
      if (snap?.year) {
        emit({
          year: snap.year,
          month: snap.month ?? null,
          day: null,
          precision: PRECISION.FULL
        });
      } else {
        emit(null);
      }
    }
  };

  // Local draft for the year input. Without this, intermediate values like
  // "1" or "198" would parse as invalid years, cause an emit(null), and the
  // controlled `value?.year ?? ''` would wipe the input on every keystroke.
  const [yearDraft, setYearDraft] = React.useState(
    value?.year ? String(value.year) : ''
  );
  const yearFocusedRef = React.useRef(false);

  // Resync draft from `value` only when the input is not focused — i.e. when
  // the parent reloaded data, the checkbox was toggled, or the precision tab
  // changed. While the user is typing, the draft owns the displayed value.
  React.useEffect(() => {
    if (yearFocusedRef.current) return;
    setYearDraft(value?.year ? String(value.year) : '');
  }, [value?.year]);

  const handleYearChange = (e) => {
    const raw = e.target.value;
    setYearDraft(raw);
    if (raw === '' || raw === null) {
      emit(null);
      return;
    }
    const year = parseInt(raw, 10);
    if (!isValidYear(year)) {
      // Hold the local draft; don't emit until the year is in range.
      return;
    }
    if (activeTab === PRECISION.MONTH) {
      const snap = lastSnapshotRef.current;
      emit({
        year,
        month: snap?.month ?? null,
        day: null,
        precision: PRECISION.MONTH
      });
    } else {
      emit({ year, month: null, day: null, precision: PRECISION.YEAR });
    }
  };

  const handleYearBlur = () => {
    yearFocusedRef.current = false;
    // Snap the draft back to the last valid year (or empty) on blur so the
    // input doesn't display a half-typed value indefinitely.
    setYearDraft(value?.year ? String(value.year) : '');
  };

  const handleYearFocus = () => {
    yearFocusedRef.current = true;
  };

  const handleMonthChange = (e) => {
    const month = parseInt(e.target.value, 10);
    const snap = lastSnapshotRef.current;
    const year = snap?.year ?? null;
    if (!year) {
      emit(null);
      return;
    }
    emit({ year, month, day: null, precision: PRECISION.MONTH });
  };

  const handleFullDateChange = (date) => {
    if (!date) {
      emit(null);
      return;
    }
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) {
      emit(null);
      return;
    }
    emit({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      precision: PRECISION.FULL
    });
  };

  // Build the JS Date for DateInput when in full mode.
  // localDateFromParts, not `new Date(y, m, d)`: the bare constructor remaps
  // years 0-99 onto 1900+year, so a part-typed "1982" flashed 1901 then 1919
  // back into the field. This was the second, independent copy of that
  // construction — see the note on the helper (TGCLOUD-1650).
  const fullDate =
    value && value.precision === PRECISION.FULL && value.month && value.day
      ? localDateFromParts(value.year, value.month, value.day)
      : null;

  const monthValue = value?.month ?? '';

  const ageText = ageResult ? formatAgeDisplay(ageResult) : '';
  const ageTooltip =
    ageResult && !ageResult.isExact
      ? `Approximate — based on ${ageResult.precision} precision`
      : '';

  return (
    <Box sx={{ width: '100%' }}>
      <FormLabel
        component="label"
        className={`form-label${required ? ' required' : ''}`}
      >
        {label}
      </FormLabel>
      {!unknownChecked && (
        <DateInput
          value={fullDate}
          onChange={handleFullDateChange}
          disabled={disabled}
          disableFuture={disableFuture}
          minDate={minDate}
        />
      )}
      {unknownChecked && (
        <Box>
          {!forceYearOnly && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: 36,
                mb: 1,
                '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 12 }
              }}
            >
              <Tab value={PRECISION.YEAR} label="Year only" />
              <Tab value={PRECISION.MONTH} label="Month + year" />
              <Tab value={PRECISION.FULL} label="Full date" />
            </Tabs>
          )}

          {activeTab === PRECISION.YEAR && (
            <TextField
              type="number"
              fullWidth
              size="small"
              placeholder="YYYY"
              value={yearDraft}
              onChange={handleYearChange}
              onFocus={handleYearFocus}
              onBlur={handleYearBlur}
              disabled={disabled}
              slotProps={{
                htmlInput: { min: MIN_YEAR, max: MAX_YEAR }
              }}
            />
          )}

          {activeTab === PRECISION.MONTH && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Select
                size="small"
                value={monthValue}
                onChange={handleMonthChange}
                disabled={disabled || !value?.year}
                displayEmpty
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="" disabled>
                  Month
                </MenuItem>
                {MONTHS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                type="number"
                size="small"
                placeholder="YYYY"
                value={yearDraft}
                onChange={handleYearChange}
                onFocus={handleYearFocus}
                onBlur={handleYearBlur}
                disabled={disabled}
                sx={{ flex: 1 }}
                slotProps={{
                  htmlInput: { min: MIN_YEAR, max: MAX_YEAR }
                }}
              />
            </Box>
          )}
        </Box>
      )}
      {!forceYearOnly && (
        <FormControlLabel
          sx={{ mt: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={unknownChecked}
              onChange={handleToggleUnknown}
              disabled={disabled}
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>Exact Date Unknown</Typography>
          }
        />
      )}
      {ageLabel && (
        <Box sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
          <Tooltip title={ageTooltip} disableHoverListener={!ageTooltip} arrow>
            <span>
              {ageLabel}: <strong>{ageText || '—'}</strong>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
